import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { useDevice } from '@atc/react-device';
import ColorSwatch from '../ColorSwatch/index.js';
import Image from '../Image/index.js';

/**
 *  Generates a radio element
 *
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const RadioWithVisual = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    src,
    colorValue,
    checked,
    labelClassName,
    imageClassName,
    option = {},
    withCheckmark,
    onChange = () => {},
    uiContext,
    image,
    ...rest
  } = _ref;
  const device = useDevice();
  const isLessThanMd = _get(device, 'lessThan.md', false);
  const {
    disabled,
    label,
    name,
    secondaryLabel,
    ...inputOptions
  } = option;
  const [isChecked, setIsChecked] = useState(checked);
  useEffect(() => {
    setIsChecked(checked);
  }, [checked]);
  const withAppBreakpoints = Object.keys(rest).filter(breakpoint => {
    const breakpoints = ['xs', 'sm', 'md', 'lg'];
    return breakpoints.includes(breakpoint) && rest[breakpoint];
  }).reduce((obj, key) => {
    obj[key] = rest[key];
    return obj;
  }, {});
  const setBootstrapClassnames = breakpoints => Object.keys(breakpoints).reduce((prevValue, val) => {
    const getBreakpointClassname = `col-${val}-${breakpoints[val]}`;
    return prevValue + ' ' + getBreakpointClassname;
  }, '');
  const getBootstrapClassnames = () => {
    if (uiContext === 'landscape') {
      return setBootstrapClassnames({
        xs: 12
      });
    }
    if (!_isEmpty(withAppBreakpoints)) {
      return setBootstrapClassnames(withAppBreakpoints);
    }
    if (src) {
      return setBootstrapClassnames({
        xs: 6,
        sm: 3,
        md: 6,
        lg: 6
      });
    }
    if (colorValue) {
      return setBootstrapClassnames({
        xs: 3,
        sm: 3,
        md: 6,
        lg: 4
      });
    }
    return setBootstrapClassnames({
      sm: 3,
      md: 6
    });
  };
  const labelClassNames = clsx(getBootstrapClassnames(), labelClassName, {
    radio: true,
    'with-visual': true,
    'margin-0': true,
    active: labelClassName?.includes('btn') && isChecked,
    'input-card-layout': uiContext === 'landscape'
  });
  const radioInputLabelClassNames = clsx('input-label radio-input-label', {
    'radio-selected': isChecked,
    'radio-unselected': !isChecked,
    'align-items-center': uiContext === 'landscape',
    'display-flex': uiContext === 'landscape'
  });
  const thumbnailDimensions = () => {
    if (uiContext === 'landscape') {
      return {
        width: 100,
        height: 50
      };
    }
    if (isLessThanMd) {
      return {
        width: 160,
        height: 78
      };
    }
    return {
      width: 118,
      height: 65
    };
  };
  return /*#__PURE__*/_jsxs("label", {
    "data-cmp": "radio",
    className: labelClassNames,
    children: [/*#__PURE__*/_jsx("input", {
      name: name,
      ref: input => ref && ref.current.push(input),
      disabled: disabled,
      type: "radio",
      checked: isChecked,
      onChange: onChange,
      ...inputOptions
    }, option.value), /*#__PURE__*/_jsxs("div", {
      className: radioInputLabelClassNames,
      children: [image ?? (src && /*#__PURE__*/_jsx("div", {
        className: imageClassName,
        style: {
          ...thumbnailDimensions()
        },
        children: /*#__PURE__*/_jsx(Image, {
          src: src,
          lazyload: true,
          alt: "" // image is only for decoration
          ,
          style: {
            width: '100%',
            height: '100%',
            objectFit: 'contain'
          }
        })
      })), colorValue && /*#__PURE__*/_jsx(ColorSwatch, {
        colorValue: colorValue,
        className: "align-self-center margin-right-3"
      }), /*#__PURE__*/_jsxs("div", {
        className: clsx({
          'text-center': uiContext !== 'landscape'
        }),
        children: [label && /*#__PURE__*/_jsx("div", {
          children: label
        }), secondaryLabel && /*#__PURE__*/_jsx("div", {
          className: "input-secondary-label",
          children: secondaryLabel
        })]
      })]
    })]
  }, option.index);
});
export default RadioWithVisual;